import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Alert,
  Bookmark,
  Button,
  MusicNote01,
  Checkbox,
  Radio,
  SearchLg,
  Stars01,
  Spinner,
  TextInput,
  Ticket01,
  Toggle,
  Users01,
} from "@opendate-app/setlist";

import { useBroadcastEditContext } from "../BroadcastEditContainer";
import DataDrivenSegmentForm from "../../Segments/DataDrivenSegmentForm";
import { toastError } from "../../../../shared/toastHelper";

const styles = {
  stack: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    marginTop: 24,
  },
  card: (selected) => ({
    display: "block",
    padding: 16,
    borderRadius: "var(--sl-radius-xl)",
    border: `${selected ? 2 : 1}px solid ${selected ? "var(--sl-border-brand)" : "var(--sl-border-secondary)"}`,
    background: selected ? "var(--sl-bg-brand-primary)" : "var(--sl-bg-primary)",
    cursor: "pointer",
  }),
  cardRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
  },
  cardText: {
    flex: 1,
    minWidth: 0,
  },
  cardLabel: {
    fontSize: 14,
    fontWeight: 600,
    color: "var(--sl-text-primary)",
  },
  cardDescription: {
    fontSize: 14,
    color: "var(--sl-text-tertiary)",
  },
  options: {
    marginTop: 12,
    paddingTop: 12,
    borderTop: "1px solid var(--sl-border-secondary)",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  refundedBuyers: {
    marginTop: 12,
    paddingTop: 12,
    borderTop: "1px solid var(--sl-border-secondary)",
  },
  optionList: {
    maxHeight: 8 * 20 + 7 * 8,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  optionRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    color: "var(--sl-text-primary)",
  },
  optionDate: {
    marginLeft: 8,
    color: "var(--sl-text-tertiary)",
  },
  optionCount: {
    marginLeft: "auto",
    color: "var(--sl-text-tertiary)",
    fontVariantNumeric: "tabular-nums",
  },
  muted: {
    fontSize: 14,
    color: "var(--sl-text-tertiary)",
  },
};

export const selectedSegmentIds = (segmentFilters) => (
  (segmentFilters[0]?.segments || []).map((segment) => segment.id)
);

export const toggleSegment = (segmentFilters, option) => {
  const parent = segmentFilters[0] || { inclusion: true, segments: [] };
  const isSelected = parent.segments.some((segment) => segment.id === option.value);
  const segments = isSelected
    ? parent.segments.filter((segment) => segment.id !== option.value)
    : [...parent.segments, { id: option.value, label: option.label, type: option.type }];

  return [{ ...parent, segments, set: true }, ...segmentFilters.slice(1)];
};

export const setIncludeRefundedBuyers = (segmentFilters, include) => {
  const parent = segmentFilters[0] || { inclusion: true, segments: [] };

  return [{ ...parent, include_refunded_buyers: include }, ...segmentFilters.slice(1)];
};

export const SEGMENT_CARDS = [
  {
    key: "buyers",
    icon: Ticket01,
    label: "Past ticket buyers",
    description: "Fans who bought tickets to specific events",
    types: ["Segments::ActualBuyers"],
    searchLabel: "Search events",
    empty: "No ticket buyers yet.",
  },
  {
    key: "genre",
    icon: MusicNote01,
    label: "Fans of a genre",
    description: "Fans who bought tickets to shows in a genre",
    types: ["Segments::GenreBuyers"],
    searchLabel: "Search genres",
    empty: "No genre audiences yet.",
  },
  {
    key: "smart",
    icon: Stars01,
    label: "Smart segments",
    description: "Audiences Opendate builds for you, like repeat attendees, early buyers and lapsed fans",
    types: [
      "Segments::Attendance",
      "Segments::Buying",
      "Segments::Value",
      "Segments::Engagement",
      "Segments::Donor",
      "Segments::Classes",
      "Segments::PassHolders",
    ],
    searchLabel: "Search smart segments",
    empty: "No smart segments yet.",
  },
  {
    key: "segment",
    icon: Bookmark,
    label: "A saved segment",
    description: "Pick from segments your team has built",
    types: ["Segments::Manual", "Segments::DataDriven"],
    searchLabel: "Search segments",
    empty: "Your team hasn't built any segments yet.",
  },
];

export const segmentCardFor = (segments) => {
  const types = (segments || []).map((segment) => segment.type);
  const match = SEGMENT_CARDS.find((card) => types.length > 0 && types.every((type) => card.types.includes(type)));

  return (match || SEGMENT_CARDS[SEGMENT_CARDS.length - 1]).key;
};

const SegmentOptions = ({ card, selectedIds, disabled, onToggle }) => {
  const { csrfToken, team } = useBroadcastEditContext();
  const [options, setOptions] = useState(null);
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState("");

  const load = (search, isCurrent = () => true) => {
    setFailed(false);
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.get(`/teams/${team.id}/segments/picker.json`, { params: { types: card.types.join(","), q: search } })
      .then(({ data }) => {
        if (isCurrent()) setOptions(data.flatMap((group) => group.options));
      })
      .catch(() => {
        if (!isCurrent()) return;
        setFailed(true);
        toastError("Could not load segments, please try again");
      });
  };

  useEffect(() => {
    let current = true;
    const debounce = setTimeout(() => load(query, () => current), query ? 300 : 0);

    return () => {
      current = false;
      clearTimeout(debounce);
    };
  }, [query]);

  return (
    <div style={styles.options}>
      <TextInput
        size="sm"
        aria-label={card.searchLabel}
        placeholder={card.searchLabel}
        leadingIcon={SearchLg}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />
      {failed ? (
        <div style={styles.muted}>
          Couldn't load segments.{" "}
          <Button variant="link-color" size="sm" onClick={() => load(query)}>Try again</Button>
        </div>
      ) : options === null ? (
        <Spinner size="sm" aria-label="Loading segments" />
      ) : options.length === 0 ? (
        <div style={styles.muted}>{query ? "No matches." : card.empty}</div>
      ) : (
        <div style={styles.optionList}>
          {options.map((option) => (
            <div key={option.value} style={styles.optionRow}>
              <Checkbox
                id={`broadcast-segment-${option.value}`}
                label={option.date ? <>{option.label}<span style={styles.optionDate}>{option.date}</span></> : option.label}
                checked={selectedIds.includes(option.value)}
                disabled={disabled}
                onChange={() => onToggle({ ...option, type: card.types[0] })}
              />
              {option.count !== null && (
                <span style={styles.optionCount}>{Number(option.count).toLocaleString()}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AudienceCard = ({ icon: Icon, label, description, selected, onSelect, children }) => (
  <div style={styles.card(selected)}>
    <label style={{ ...styles.cardRow, margin: 0, cursor: "pointer" }}>
      <Radio name="broadcast-audience" checked={selected} onChange={onSelect} />
      <Icon width={20} height={20} color="var(--sl-fg-quaternary)" />
      <div style={styles.cardText}>
        <div style={styles.cardLabel}>{label}</div>
        <div style={styles.cardDescription}>{description}</div>
      </div>
    </label>
    {selected && children}
  </div>
);

const RecipientsStep = () => {
  const {
    csrfToken,
    team,
    venueOwnership,
    broadcast,
    updateBroadcast,
    recipientsCount,
    segmentFilters,
    setSegmentFilters,
    createSegmentFilter,
    updateSegmentFilter,
    destroySegmentFilter,
    fetchRecipientCount,
    segment,
    setSegment,
    fanFilterGroupId,
    setFanFilterGroupId,
    isEmailTemplate,
    aiSegmentBuilder,
  } = useBroadcastEditContext();

  const [savingSegments, setSavingSegments] = useState(false);
  const [activeCard, setActiveCard] = useState(segmentCardFor(broadcast.parent_segment_filter?.segments));

  const mode = broadcast.recipient_type === "all_fans"
    ? "everyone"
    : broadcast.recipient_type === "conditional" && broadcast.segment_type === "new"
      ? "custom"
      : broadcast.recipient_type === "conditional"
        ? "segment"
        : null;

  const venueName = venueOwnership.venueable.name;
  const selectedIds = selectedSegmentIds(segmentFilters);

  const selectEveryone = () => {
    updateBroadcast({ recipient_type: "all_fans" }, false, () => {
      setSegment({});
      setFanFilterGroupId(null);
    });
  };

  const selectSegmentCard = (cardKey) => {
    const parent = segmentFilters[0];
    if (parent?.id && segmentCardFor(parent.segments) !== cardKey) destroySegmentFilter(parent, { withChildren: true });

    setActiveCard(cardKey);
    if (mode !== "segment") updateBroadcast({ recipient_type: "conditional", segment_type: "existing" }, false);
  };

  const buildCustomConditions = () => {
    updateBroadcast({
      recipient_type: "conditional",
      segment_type: "new",
      ...(broadcast.segment?.id ? {} : {
        broadcast_segment_attributes: {
          broadcast_id: broadcast.id,
          segment_attributes: {
            name: `${broadcast.name} - Custom Segment`,
            type: "Segments::DataDriven",
            team_id: team.id,
            fan_filter_group_attributes: {
              match_condition: "All",
              team_id: team.id,
            },
          },
        },
      }),
    }, false);
  };

  const onToggleSegment = (option) => {
    const next = toggleSegment(segmentFilters, option);
    const parent = next[0];

    if (parent.segments.length === 0 && !parent.id) {
      setSegmentFilters([]);
      return;
    }

    const save = parent.segments.length === 0
      ? destroySegmentFilter(parent, { withChildren: true })
      : parent.id
        ? updateSegmentFilter(next, false)
        : createSegmentFilter(next, false);

    setSavingSegments(true);
    save.finally(() => setSavingSegments(false));
  };

  const onToggleRefundedBuyers = (include) => {
    const next = setIncludeRefundedBuyers(segmentFilters, include);

    if (!next[0].id) {
      setSegmentFilters(next);
      return;
    }

    setSavingSegments(true);
    updateSegmentFilter(next, false).finally(() => setSavingSegments(false));
  };

  return (
    <div style={styles.stack}>
      <AudienceCard
        icon={Users01}
        label="Everyone"
        description={mode === "everyone"
          ? `All ${recipientsCount} fans in the ${venueName} workspace`
          : `All fans in the ${venueName} workspace`}
        selected={mode === "everyone"}
        onSelect={selectEveryone}
      />

      {SEGMENT_CARDS.map((card) => {
        const selected = mode === "segment" && activeCard === card.key;

        return (
          <AudienceCard
            key={card.key}
            icon={card.icon}
            label={card.label}
            description={selected && selectedIds.length > 0
              ? `${selectedIds.length} selected · ${recipientsCount} fans`
              : card.description}
            selected={selected}
            onSelect={() => selectSegmentCard(card.key)}
          >
            <SegmentOptions
              card={card}
              selectedIds={selectedIds}
              disabled={savingSegments}
              onToggle={onToggleSegment}
            />
            {card.key === "buyers" && (
              <div style={styles.refundedBuyers}>
                <Checkbox
                  id="broadcast-include-refunded-buyers"
                  label="Include refunded buyers"
                  checked={segmentFilters[0]?.include_refunded_buyers || false}
                  disabled={savingSegments}
                  onChange={(event) => onToggleRefundedBuyers(event.target.checked)}
                />
                <div style={{ ...styles.muted, marginTop: 4 }}>
                  For cancellation or postponement notices.
                </div>
              </div>
            )}
          </AudienceCard>
        );
      })}

      <div style={styles.muted}>
        Need something more specific?{" "}
        <Button variant="link-color" size="sm" onClick={buildCustomConditions}>
          Build custom conditions
        </Button>
      </div>

      {mode === "custom" && fanFilterGroupId ? (
        <DataDrivenSegmentForm
          csrfToken={csrfToken}
          team={team}
          segment={segment}
          setSegment={setSegment}
          fanFilterGroupId={fanFilterGroupId}
          setFanFilterGroupId={setFanFilterGroupId}
          showSegmentNameInput={true}
          onValidChangeCallback={fetchRecipientCount}
          aiSegmentBuilder={aiSegmentBuilder}
        />
      ) : null}

      {isEmailTemplate && (
        <div style={{ ...styles.card(false), cursor: "default" }}>
          <Toggle
            label="Engaged fans only"
            checked={broadcast.target_engaged_audience || false}
            onChange={(event) => updateBroadcast({ target_engaged_audience: event.target.checked }, false)}
          />
          <div style={{ ...styles.muted, marginTop: 4 }}>
            Skip fans who haven't opened any email in 3 months. Protects your deliverability.
          </div>
        </div>
      )}

      {isEmailTemplate && mode === "everyone" && !broadcast.target_engaged_audience && (
        <Alert
          color="warning"
          title="Sending to everyone can hurt your sending reputation."
          description="Turning on engaged fans only helps."
        />
      )}

      {!isEmailTemplate && mode === "everyone" && (
        <Alert
          color="warning"
          title="Texting everyone at once can get your number flagged by carriers."
          description="A targeted audience performs better."
        />
      )}
    </div>
  );
};

export default RecipientsStep;
